import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import FunnelLayout from "./FunnelLayout";
import "./CommonNewFunnel.css";
import TrustPilot from "../../assets/images/TrustpilotReviews.png";
import HippaCompliant from "../../assets/images/HIPAA-Compliant.png";
import MadeInUsa from "../../assets/images/Made-in-USA-Badge.png";

import { createNewPatient, submitIntakeForm } from "../../services/api";

const LS_KEY_STEP1 = "glp1_intake_step1";
const LS_KEY_STEP10 = "glp1_intake_step10";
const LS_KEY_STEP11 = "glp1_intake_step11";

const fireTrackdeskLead = (email: string) => {
  try {
    const w = window as any;
    if (typeof w.trackdesk !== "function") return;

    // ✅ dedupe so it won't fire multiple times on refresh/back
    const key = `td_lead_${email.trim().toLowerCase()}`;
    if (localStorage.getItem(key) === "1") return;

    w.trackdesk("claya-affiliate", "conversion", {
      conversionType: "lead",
      customParams: {
        advS1: email.trim(),
      },
    });

    localStorage.setItem(key, "1");
  } catch {
    // ignore
  }
};

// ✅ add any extra step keys you use (5a etc)
const INTAKE_STEP_KEYS = [
  "glp1_intake_step1",
  "glp1_intake_step2",
  "glp1_intake_step3",
  "glp1_intake_step4",
  "glp1_intake_step5",
  "glp1_intake_step5a",
  "glp1_intake_step6",
  "glp1_intake_step7",
  "glp1_intake_step8",
  "glp1_intake_step9",
  "glp1_intake_step10",
  "glp1_intake_step11",
];

type Step10State = {
  firstName?: string;
  lastName?: string;
  stateCode?: string;
  stateName?: string;
  state?: string; // old
};

type Step11State = {
  email: string;
  phone: string; // digits only
  consent: boolean; // send as isSubscribed
};

const DEFAULT: Step11State = {
  email: "",
  phone: "",
  consent: false,
};

const EmailIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
    style={{ height: "24px", width: "24px", marginTop: "2px" }}
  >
    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
  </svg>
);

const PhoneIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="rgb(156, 163, 175)"
    aria-hidden="true"
    style={{ color: "rgb(156 163 175)", marginTop: "6px" }}
  >
    <path
      d="M6.5 3.5l3 1.2c.6.25.9.92.7 1.53l-1 2.9c-.15.45-.05.95.28 1.3l4.6 4.6c.35.33.85.43 1.3.28l2.9-1c.61-.2 1.28.1 1.53.7l1.2 3c.25.6-.02 1.3-.6 1.6-1.25.64-2.7.9-4.1.5-3.1-.9-6.5-3.6-9.2-6.3S3.9 9.9 3 6.8c-.4-1.4-.14-2.85.5-4.1.3-.58 1-.85 1.6-.6Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
      opacity="0.55"
    />
  </svg>
);

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const normalizePhoneDigits = (value: string) =>
  value.replace(/[^\d]/g, "").slice(0, 10);

const formatPhoneUS = (digits: string) => {
  const d = normalizePhoneDigits(digits);
  if (d.length === 0) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

// ---------- LS helpers ----------
const readLSJson = (key: string) => {
  const raw = localStorage.getItem(key);
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

const normalizeStateCode = (v: any) =>
  String(v || "")
    .trim()
    .toUpperCase();

// ---------- eligibility helpers ----------
const syncStates = ["AR", "DC", "DE", "LA", "MS", "NM", "RI", "WV"];

// ✅ warnHQ2 = ONLY exclusion (disqualify)
const warnHQ2 = new Set<string>([
  "Gallbladder disease",
  "Type 2 diabetes (on insulin)",
  "Type 1 diabetes",
  "Diabetic retinopathy (diabetic eye disease)",
  "Use of the blood thinner warfarin (Coumadin/Jantoven)",
  "History of or current pancreatitis",
  "Human immunodeficiency virus (HIV)",
]);

// ✅ NEW: SYNC triggers from HQ2 (Seizures is here in Step-1)
const syncHQ2Triggers = new Set<string>([
  "Seizures",
  "Tumor/infection in brain/spinal cord",
  "Elevated resting heart rate (tachycardia)",
  "Coronary artery disease or heart attack/stroke in last 2 years",
  "Congestive heart failure",
  "Hospitalization within the last 1 year",
]);

// ✅ OPTIONAL: SYNC triggers from HQ1 if you ever need (currently empty on purpose)
// because HQ1 is mostly disqualify in your UI.
// Keep this here so you can add later without changing structure.
const syncHQ1Triggers = new Set<string>([]);

const calculateAgeFromDob = (dobString: any) => {
  if (!dobString || typeof dobString !== "string") return NaN;
  const [month, day, year] = dobString.split("/").map(Number);
  if (!month || !day || !year) return NaN;

  const birthDate = new Date(year, month - 1, day);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
};

// ✅ TRUE => SYNC visit (BOOK appointment)
const isEligibleToBookAppointmentNew = (params: {
  step1: any;
  stateCode: string;
}) => {
  const { step1, stateCode } = params;

  if (syncStates.includes(stateCode)) return true;

  const age = calculateAgeFromDob(step1?.dob);
  if (Number.isFinite(age) && age >= 65 && age <= 74) return true;

  if (step1?.Blood_pressure_range === "≥140/90 (High Stage 2)") return true;

  if (
    step1?.Average_resting_heart_rate ===
      "101 to 110 beats per minute (Slightly Fast)" ||
    step1?.Average_resting_heart_rate === ">110 beats per minute (Fast)"
  ) {
    return true;
  }

  if (
    step1?.Q1_Within_the_last_3_months_have_you_taken_opiate_pain_medications_and_or_opiate_based_street_drugs ===
    "Yes"
  ) {
    return true;
  }

  if (step1?.Q2_Have_you_had_prior_weight_loss_surgeries === "Yes") return true;

  // ✅ HQ2: SYNC triggers (Seizures lives here)
  const h2 = Array.isArray(
    step1?.Health_Questions_2_Do_any_of_these_apply_to_you
  )
    ? step1.Health_Questions_2_Do_any_of_these_apply_to_you
    : [];
  if (h2.some((x: string) => syncHQ2Triggers.has(x))) return true;

  // ✅ HQ1: optional SYNC triggers (currently empty)
  const h1 = Array.isArray(
    step1?.Health_Questions_1_Do_any_of_these_apply_to_you
  )
    ? step1.Health_Questions_1_Do_any_of_these_apply_to_you
    : [];
  if (h1.some((x: string) => syncHQ1Triggers.has(x))) return true;

  return false;
};

// ✅ EXCLUSION = ONLY Step-1 "For your safety" warnings
const isExcludedFromStep1Warnings = (step1: any) => {
  const gender = String(step1?.gender || "");
  const femaleArr = Array.isArray(
    step1?.Do_any_of_these_apply_to_you_female_conditions
  )
    ? step1.Do_any_of_these_apply_to_you_female_conditions
    : [];

  const femaleWarning =
    gender === "Female" &&
    (femaleArr.includes(
      "Currently or possibly pregnant, or actively trying to become pregnant"
    ) ||
      femaleArr.includes("Breastfeeding or bottle-feeding with breastmilk"));

  const h1 = Array.isArray(
    step1?.Health_Questions_1_Do_any_of_these_apply_to_you
  )
    ? step1.Health_Questions_1_Do_any_of_these_apply_to_you
    : [];
  const health1Warning = h1.length > 0 && !h1.includes("None of the above");

  const h2 = Array.isArray(
    step1?.Health_Questions_2_Do_any_of_these_apply_to_you
  )
    ? step1.Health_Questions_2_Do_any_of_these_apply_to_you
    : [];
  const health2Warning = h2.some((x: string) => warnHQ2.has(x));

  const bpWarning = step1?.Blood_pressure_range === "≥140/90 (High Stage 2)";

  return femaleWarning || health1Warning || health2Warning || bpWarning;
};

const NewFunnelStep11: React.FC = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState<Step11State>(DEFAULT);
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const step10 = useMemo((): Step10State => {
    const saved = localStorage.getItem(LS_KEY_STEP10);
    if (!saved) return {};
    try {
      return JSON.parse(saved);
    } catch {
      return {};
    }
  }, []);

  const firstName = (step10.firstName || "").trim();
  const lastName = (step10.lastName || "").trim();

  // ✅ IMPORTANT: normalize stateCode so syncStates match correctly
  const stateCode = normalizeStateCode(step10.stateCode || step10.state);
  const stateName = (step10.stateName || "").trim();

  const uuidValue = useMemo(() => {
    const raw = localStorage.getItem("uuid");
    if (!raw) return "";
    try {
      return JSON.parse(raw)?.value || "";
    } catch {
      return "";
    }
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem(LS_KEY_STEP11);
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as Partial<Step11State>;
        setForm({
          email: String(parsed.email || ""),
          phone: normalizePhoneDigits(String(parsed.phone || "")),
          consent: Boolean(parsed.consent),
        });
      } catch {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(LS_KEY_STEP11, JSON.stringify(form));
  }, [form]);

  // ✅ compute + store modality as soon as we have stateCode + step1
  useEffect(() => {
    if (!stateCode) return;
    const step1 = readLSJson(LS_KEY_STEP1);
    const eligible = isEligibleToBookAppointmentNew({ step1, stateCode });
    const mwl_modality = eligible ? "sync_visit" : "async_visit";
    localStorage.setItem("mwl_modality", mwl_modality);
  }, [stateCode]);

  const emailOk = isValidEmail(form.email);
  const phoneOk = form.phone.length === 10;
  const canSubmit = emailOk && phoneOk && form.consent;

  const buildAllStepsPayload = () => {
    const merged: Record<string, any> = {};
    for (const k of INTAKE_STEP_KEYS) {
      const obj = readLSJson(k);
      if (obj && typeof obj === "object") {
        Object.assign(merged, obj);
      }
    }
    return merged;
  };

  const onSubmit = async () => {
    setTouched(true);
    setApiError("");
    if (!canSubmit) return;

    if (!firstName || !lastName || !stateCode) {
      setApiError(
        "Missing name or state from previous step. Please go back and complete Step 10."
      );
      return;
    }

    try {
      setLoading(true);

      const productCategory = "GLP1";
      const phoneFormatted = formatPhoneUS(form.phone);
      const isSubscribed = form.consent;

      const res = await createNewPatient(
        firstName,
        lastName,
        form.email.trim(),
        phoneFormatted,
        isSubscribed,
        productCategory,
        uuidValue
      );

      let data: any = null;
      try {
        data = await res.json();
      } catch (e) {
        console.log(e);
      }

      if (!res.ok && res.status !== 409) {
        const msg = data?.error || "Failed to create patient.";
        setApiError(msg);
        return;
      }

      fireTrackdeskLead(form.email.trim());

      localStorage.setItem(
        "firstName",
        JSON.stringify({ key: "firstName", value: firstName })
      );
      localStorage.setItem(
        "lastName",
        JSON.stringify({ key: "lastName", value: lastName })
      );
      localStorage.setItem(
        "email",
        JSON.stringify({ key: "email", value: form.email.trim() })
      );
      localStorage.setItem(
        "phone",
        JSON.stringify({ key: "phone", value: phoneFormatted })
      );
      localStorage.setItem(
        "state",
        JSON.stringify({ key: "state", value: stateCode })
      );
      localStorage.setItem(
        "fullState",
        JSON.stringify({ key: "fullState", value: stateName || stateCode })
      );
      localStorage.setItem(
        "isSubscribed",
        JSON.stringify({ key: "isSubscribed", value: isSubscribed })
      );

      const step1 = readLSJson(LS_KEY_STEP1);
      const allSteps = buildAllStepsPayload();

      // ✅ remove empty female_conditions so API won't receive it
      if (
        Array.isArray(
          allSteps?.Do_any_of_these_apply_to_you_female_conditions
        ) &&
        allSteps.Do_any_of_these_apply_to_you_female_conditions.length === 0
      ) {
        delete allSteps.Do_any_of_these_apply_to_you_female_conditions;
      }

      console.log("STATE CODE:", stateCode);
      console.log(
        "STEP1 HQ2:",
        step1?.Health_Questions_2_Do_any_of_these_apply_to_you
      );
      console.log("STEP1 VALUES:", {
        dob: step1?.dob,
        bp: step1?.Blood_pressure_range,
        hr: step1?.Average_resting_heart_rate,
        q1: step1?.Q1_Within_the_last_3_months_have_you_taken_opiate_pain_medications_and_or_opiate_based_street_drugs,
        q2: step1?.Q2_Have_you_had_prior_weight_loss_surgeries,
      });

      // ✅ use same eligibility logic, but with normalized stateCode
      const isEligibleToBook = isEligibleToBookAppointmentNew({
        step1,
        stateCode,
      });

      console.log("ELIGIBLE TO BOOK:", isEligibleToBook);

      const mwl_modality = isEligibleToBook ? "sync_visit" : "async_visit";
      localStorage.setItem("mwl_modality", mwl_modality);

      const isExcluded = isExcludedFromStep1Warnings(step1);

      const submissionPayload = {
        ...allSteps,

        mwl_modality,
        preferred_treatment: allSteps?.preferred_treatment || "",

        uuid: uuidValue,
        product_category: productCategory,
        first_name: firstName,
        last_name: lastName,
        state: stateCode,
        email: form.email.trim(),
        phone_number: phoneFormatted,
        email_subscribed: isSubscribed,
      };

      // ✅ Store intake payload for Step 12 submission
      localStorage.setItem(
        "intake_submission_payload",
        JSON.stringify({
          key: "intake_submission_payload",
          value: submissionPayload,
        })
      );

      // ✅ Store exclusion flag
      localStorage.setItem(
        "intake_excluded",
        JSON.stringify({
          key: "intake_excluded",
          value: isExcluded,
        })
      );

      // mwl_modality is already being stored — keep that

      // await submitIntakeForm(
      //   submissionPayload,
      //   form.email.trim(),
      //   productCategory,
      //   uuidValue
      // );

      // if (isExcluded) {
      //   navigate("/glp1/intake-form/disqualified");
      // } else {
      //   // keep your route unchanged (UI flow stays exactly as you had)
      //   navigate("/glp1/intake-form/step-11");
      // }
      navigate("/glp1/intake-form/step-11");
    } catch (e: any) {
      console.error("step11 submit failed:", e);
      setApiError(e?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const displayPhone = formatPhoneUS(form.phone);

  return (
    <FunnelLayout>
      <h1
        className="title-step-1  funnel-q-title-heading"
        style={{ color: "#000" }}
      >
        {firstName ? (
          <>
            <span>{firstName}, </span>how can you be reached if necessary?
          </>
        ) : (
          <>How can you be reached if necessary?</>
        )}
      </h1>

      <p className="subtitle-step-1" style={{ marginTop: "10px" }}>
        Our medical teams and pharmacy use email and text for patient
        communication.
      </p>

      <div className="funnel-section" style={{ marginTop: "18px" }}>
        {/* Email */}
        <div className="funnel-field">
          <div className="funnel-field__label">Email</div>

          <div className="funnel-input-with-icon">
            <span className="funnel-input-icon" aria-hidden="true">
              <EmailIcon />
            </span>

            <input
              className="funnel-input"
              inputMode="email"
              value={form.email}
              onChange={(e) =>
                setForm((p) => ({ ...p, email: e.target.value }))
              }
            />
          </div>

          {touched && !emailOk && (
            <div className="funnel-error">
              Please enter a valid email address
            </div>
          )}
        </div>

        {/* Phone */}
        <div className="funnel-field" style={{ marginTop: "16px" }}>
          <div className="funnel-field__label">Phone Number</div>

          <div className="funnel-input-with-icon">
            <span className="funnel-input-icon" aria-hidden="true">
              <PhoneIcon />
            </span>

            <input
              className="funnel-input"
              inputMode="tel"
              placeholder="(XXX) XXX-XXXX"
              value={displayPhone}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  phone: normalizePhoneDigits(e.target.value),
                }))
              }
            />
          </div>

          {touched && !phoneOk && (
            <div className="funnel-error">
              Please enter a valid phone number
            </div>
          )}
        </div>

        {/* Consent */}
        <div className="consent-row" style={{ marginTop: "14px" }}>
          <button
            type="button"
            className={`funnel-option funnel-checkbox ${
              form.consent ? "is-selected" : ""
            }`}
            onClick={() => setForm((p) => ({ ...p, consent: !p.consent }))}
          >
            <span
              className={`funnel-control checkbox ${
                form.consent ? "is-selected" : ""
              }`}
            >
              <span className="tick" />
            </span>

            <span className="funnel-option__label consent-text">
              I understand that my information is never shared, is protected by
              HIPAA and I agree to receive communications by text message
              regarding appointments and product related messages from CLAYA and
              its medical partners, and agree to the{" "}
              <a
                href="https://claya.com/terms-of-use/"
                target="_blank"
                rel="noreferrer"
              >
                terms
              </a>{" "}
              and{" "}
              <a
                href="https://claya.com/privacy-policy/"
                target="_blank"
                rel="noreferrer"
              >
                privacy policies
              </a>{" "}
              and can opt-out at anytime by replying STOP or ask for more
              information by replying HELP. Message frequency varies. Message
              and data rates may apply.
            </span>

            <input
              className="sr-only"
              type="checkbox"
              checked={form.consent}
              onChange={() => setForm((p) => ({ ...p, consent: !p.consent }))}
            />
          </button>
        </div>

        {touched && !form.consent && (
          <div className="funnel-error" style={{ marginTop: "10px" }}>
            Please accept to continue
          </div>
        )}

        {apiError && (
          <div className="funnel-error" style={{ marginTop: "12px" }}>
            {apiError}
          </div>
        )}
      </div>

      <div className="funnel-divider" style={{ marginTop: "18px" }} />

      <button
        className="next-btn"
        style={{ marginBottom: "20px" }}
        onClick={onSubmit}
        disabled={!canSubmit || loading}
      >
        {loading ? "Processing..." : "Continue"}
      </button>

      <div className="logo-badges">
        <img className="logo-badge-pilot" src={TrustPilot} alt="TrustPilot" />
        <img
          className="logo-badge-hippa"
          src={HippaCompliant}
          alt="HippaCompliant"
        />
        <img className="logo-badge-usa" src={MadeInUsa} alt="MadeInUsa" />
      </div>
    </FunnelLayout>
  );
};

export default NewFunnelStep11;
